// ============================================================================
// usePhotoSrc - React hook for rendering photos from any storage backend
// ----------------------------------------------------------------------------
// Handles three storage formats seamlessly:
//   1. Legacy: photo.data is a base64 data URL string → use directly
//   2. Blob store: photo.blobId references a Blob in photo_blobs table → ObjectURL
//   3. S3: photo.s3Key references a remote file → signed URL
//
// Components use: const src = usePhotoSrc(photo);
// Then render: <img src={src} />
//
// ObjectURLs are automatically created and revoked on unmount.
//
// DEC-COUNCIL-007 / Candidate C6 Slice 3 (degraded-fallback signal):
// usePhotoSrcDetailed(photo) → { src, source, degraded } so UI can surface
// "blob unavailable, falling back to S3" or "S3 fetch failed". The plain
// usePhotoSrc still returns just the src string for backward-compat.
//
// DEC-COUNCIL-007 / Candidate C6 Slice 2 (cascade collapse): `isUsableDirectUrl`
// is sourced from `photo-resolution-policy.js` so the predicate stays in lockstep
// with the sync cascade in photo-client-images.js. The two cascades remain
// SEPARATE (render-side async vs sync inspection serve different audiences) but
// the shared primitives no longer drift.
// ============================================================================

import { useState, useEffect, useRef } from 'react';
import { isUsableDisplayUrl as isUsableDirectUrl } from '../services/photo-resolution-policy.js';
import { isRawMinioUrl } from './minio-url.js';
import {
    candidateFileUnavailableReason,
    fileReferenceUnavailableReason,
} from '../services/file-reference-health.js';

// Structural shape of the photo records these hooks read. Every field is
// optional + loosely typed because photo objects arrive from many producers
// (legacy base64, blob store, S3, session previews) and the runtime code
// already branches defensively on each field's presence/type.
interface PhotoLike {
    id?: unknown;
    name?: unknown;
    data?: unknown;
    dataUrl?: unknown;
    url?: unknown;
    previewUrl?: unknown;
    signedUrl?: unknown;
    thumbnailUrl?: unknown;
    blobId?: unknown;
    s3Key?: unknown;
    fileMetadataId?: unknown;
    unavailableReason?: unknown;
    availability_state?: unknown;
    availability_reason?: unknown;
    availabilityState?: unknown;
    availabilityReason?: unknown;
}

// Detailed-variant state shape (usePhotoSrcDetailed).
type PhotoSrcSource = 'inline' | 'blob' | 's3' | 'file-metadata' | 'thumbnail' | 'session-url' | null;
interface PhotoSrcState {
    src: string | null;
    source: PhotoSrcSource;
    degraded: boolean;
    unavailableReason?: string | null;
}

// Stable-ish identity for a photo record across the many producer shapes.
// Lives here (moved from PhotoCrossfade, which re-exports it) because two
// display-layer decisions share the notion: WHEN a crossfade should fire, and
// WHEN a hook's resolved src must be cleared because the record it belonged to
// was swapped out from under the component.
export function photoIdentity(photo: any): any {
    if (photo == null) return null;
    return photo.id
        ?? photo.blobId
        ?? photo.s3Key
        ?? photo.fileMetadataId
        ?? photo.previewUrl
        ?? photo.signedUrl
        ?? photo.url
        ?? photo.thumbnailUrl
        ?? photo.name
        ?? photo;
}

/**
 * Stable React key for a photo tile. Index keys let React reuse a tile's
 * component instance across a project switch, so the old photo's resolved src
 * stays painted while the new record resolves — the display-layer half of the
 * v5.46.72 cross-project photo-bleed class. `photo.id` is trusted as unique
 * when present; derived identities (blobId/s3Key/url/name) get the index
 * appended as a collision backstop (two same-named files with no durable refs
 * must not produce duplicate keys).
 */
export function stablePhotoTileKey(photo: PhotoLike | null | undefined, index: number): string {
    const id = photo?.id;
    if (id != null && id !== '') return `photo-${String(id)}`;
    const identity = photoIdentity(photo);
    if (identity != null && typeof identity !== 'object') return `photo-${String(identity)}-${index}`;
    return `photo-idx-${index}`;
}

// `getFileUrl` in file-upload-service.js is an `async` function (so it returns
// a Promise at runtime) but its JSDoc declares `@returns {string}`. Under
// allowJs + checkJs:false, TS trusts the (incorrect) JSDoc and types the
// import as `(s3Key: string) => string`, which makes the `.then(...)` chain
// below a type error. We cannot edit that file from this slice, so we annotate
// the dynamic-import binding with its TRUE runtime signature via a
// double-assertion (`as unknown as`, the only legal bridge between the
// JSDoc-derived type and the real Promise type). Type-only; no behaviour
// change.
type GetFileUrlFn = (s3Key: unknown) => Promise<string | null>;
type GetPhotoBlobFn = (blobId: unknown, expectedProjectId?: string | null) => Promise<Blob | null>;

async function getFileMetadataPreviewUrl(fileMetadataId: unknown): Promise<string | null> {
    if (typeof fileMetadataId !== 'string' || !fileMetadataId.trim()) return null;
    const { DataServiceAPI } = await import('../services/data-service-api');
    const meta = await DataServiceAPI.files.getPreviewMeta(fileMetadataId.trim());
    if (!meta?.url || meta.previewType === 'unsupported') return null;
    return String(meta.url);
}

// A photo whose every source failed once used to stay blank until the page
// reloaded, and Print / Download PDF captured that blank (Harbourline,
// 2026-10-25: six "NO IMAGE" cards on a PDF whose photos the cloud served
// fine). PhotoThumbnail now retries a failed lookup on its own, and this
// signal makes every failed thumbnail try again at once, as print capture does
// before it waits for photos.
export const PHOTO_SOURCE_RETRY_EVENT = 'qm:photo-source-retry';

// A thumbnail still loading, or failed with no known-missing file. Print
// capture waits on these as well as on empty <img> tags.
export const UNRESOLVED_PHOTO_SELECTOR = '[data-photo-state="loading"], [data-photo-state="failed"]';

export function requestPhotoSourceRetry(): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event(PHOTO_SOURCE_RETRY_EVENT));
}

function cachedReferenceUnavailableReason(reference: unknown): string | null {
    return typeof reference === 'string' && reference.trim()
        ? fileReferenceUnavailableReason(reference.trim())
        : null;
}

/**
 * Get a displayable image source for a photo object.
 * Handles legacy base64, Blob store, and S3 paths automatically.
 *
 * @param {Object} photo - Photo object with `data`, `blobId`, or `s3Key`
 * @param {string|null} expectedProjectId - When set, refuse a blob owned by another project
 * @returns {string|null} Image source URL for <img src={}>
 */
export function usePhotoSrc(
    photo: PhotoLike | null | undefined,
    expectedProjectId: string | null = null,
): string | null {
    const [src, setSrc] = useState<string | null>(null);
    const currentIdentity = photoIdentity(photo);
    const lastIdentityRef = useRef<{ photo: unknown; projectId: string | null } | undefined>(undefined);
    const resolvedStampRef = useRef<{ photo: unknown; projectId: string | null }>({
        photo: currentIdentity,
        projectId: expectedProjectId,
    });

    useEffect(() => {
        // v5.46.73 display-layer hygiene: when the RECORD this component shows
        // is swapped for a different one (tile reuse across a project switch,
        // index-keyed lists), the previous photo's resolved src must not stay
        // painted while the new record resolves asynchronously — that window
        // showed the PRIOR photo's bytes (the cross-project bleed class,
        // v5.46.72). Same-record enrichment (upload stamps s3Key, migration
        // adds blobId → identity unchanged via photo.id) keeps the current src
        // while re-resolving, so tiles don't flash after a save. 1-up viewers
        // that want a no-flash transition use PhotoCrossfade, which mounts a
        // NEW layer per photo (stable identity per layer) instead of relying
        // on this hook holding a stale src.
        const prevIdentity = lastIdentityRef.current;
        const identity = photoIdentity(photo);
        const stamp = { photo: identity, projectId: expectedProjectId };
        const commitSrc = (value: string | null) => {
            resolvedStampRef.current = stamp;
            setSrc(value);
        };
        lastIdentityRef.current = stamp;
        if (!photo) { commitSrc(null); return; }
        if (!prevIdentity
            || !Object.is(identity, prevIdentity.photo)
            || expectedProjectId !== prevIdentity.projectId) commitSrc(null);
        // A terminal receipt belongs to one cloud reference, not to every
        // independent source carried by the photo record. In particular, an
        // unavailable s3Key must not suppress inline recovery bytes, a local
        // blob, or a separate file_metadata receipt. Source-specific checks
        // below skip only the unavailable reference and keep the failover
        // chain moving; this display hook never clears or rewrites the receipt.

        let objectUrl: string | null = null;
        let cancelled = false;
        // isUsableDirectUrl is imported from photo-resolution-policy.js so the
        // predicate stays in lockstep with photo-client-images.js (Slice 2).

        // Cross-device guard (2026-06-05): a stored raw MinIO :9010 URL is
        // unreachable from any device that can't hit the host MinIO port —
        // rewrite it to a same-origin proxy URL (via the s3 key it embeds)
        // before display. Non-MinIO URLs (data: / blob: / proxy) set directly.
        const setSafeSrc = (url: string, onUnsafe: () => void = () => commitSrc(null)) => {
            if (cancelled) return;
            if (isRawMinioUrl(url)) {
                import('../services/file-upload-service')
                    .then(({ toSameOriginDisplayUrl }) => toSameOriginDisplayUrl(url))
                    .then((safe) => {
                        if (cancelled) return;
                        if (safe && !isRawMinioUrl(safe)) commitSrc(safe);
                        else onUnsafe();
                    })
                    .catch(() => { if (!cancelled) onUnsafe(); });
            } else {
                commitSrc(url);
            }
        };

        // Priority 1: Inline data URL. Ignore stale blob: URLs saved from a
        // previous browser session; durable refs below are more trustworthy.
        // (isUsableDirectUrl validates string-ness at runtime; the `as string`
        // casts below are compile-erased and reflect that guarantee.)
        if (isUsableDirectUrl(photo.data) && !isRawMinioUrl(photo.data)) {
            commitSrc(photo.data as string);
            return;
        }
        if (isUsableDirectUrl(photo.dataUrl) && !isRawMinioUrl(photo.dataUrl)) {
            commitSrc(photo.dataUrl as string);
            return;
        }

        // Priority 3–5: sequential fallback chain. Mirrors usePhotoSrcDetailed
        // (Slice 3) — blob is the canonical durable source; only fall through
        // to s3 / session-url if blob is absent or fails. v5.31.2 fix: previous
        // implementation ran priority 3 (blob) and priority 4 (s3) as parallel
        // uncoordinated promises, so the slower one would clobber the faster
        // one (typically: blob resolves fast then s3 resolves seconds later +
        // overwrites with a less-canonical signed URL, then the next render's
        // cleanup revokes the still-displayed blob ObjectURL → broken image).
        // Sequential `tryBlob.finally(...)` with a `blobFound` guard restores
        // the priority ordering at runtime, matching the documented intent.
        const expectedBlob = !!photo.blobId;
        let blobFound = false;

        const tryBlob: Promise<string | null> = expectedBlob
            ? import('../services/photo-blob-store').then(({ getPhotoBlob }) => {
                if (cancelled) return null;
                return (getPhotoBlob as unknown as GetPhotoBlobFn)(photo.blobId, expectedProjectId).then(blob => {
                    if (cancelled || !blob) return null;
                    objectUrl = URL.createObjectURL(blob);
                    blobFound = true;
                    commitSrc(objectUrl);
                    return objectUrl;
                });
            })
            : Promise.resolve(null);

        const setSessionFallback = () => {
            if (cancelled) return;
            const candidates = [
                photo.thumbnailUrl,
                photo.previewUrl,
                photo.signedUrl,
                photo.url,
                photo.data,
                photo.dataUrl,
            ].filter(isUsableDirectUrl) as string[];
            const tryCandidate = (index: number) => {
                if (cancelled) return;
                const candidate = candidates[index];
                if (candidate) {
                    setSafeSrc(candidate, () => tryCandidate(index + 1));
                } else if (typeof photo.data === 'string' && photo.data.startsWith('blob:')) {
                    commitSrc(photo.data);
                } else {
                    commitSrc(null);
                }
            };
            tryCandidate(0);
        };

        const tryFileMetadata = () => {
            if (!photo.fileMetadataId || cachedReferenceUnavailableReason(photo.fileMetadataId)) {
                setSessionFallback();
                return;
            }
            getFileMetadataPreviewUrl(photo.fileMetadataId)
                .then((previewUrl) => {
                    if (cancelled) return;
                    if (previewUrl) setSafeSrc(previewUrl, setSessionFallback);
                    else setSessionFallback();
                })
                .catch(() => setSessionFallback());
        };

        tryBlob.finally(() => {
            if (cancelled || blobFound) return;
            // Priority 4: S3 key (when backend is active, blob unavailable).
            if (photo.s3Key && !cachedReferenceUnavailableReason(photo.s3Key)) {
                import('../services/file-upload-service').then(({ getFileUrl }) => {
                    if (cancelled) return;
                    (getFileUrl as unknown as GetFileUrlFn)(photo.s3Key).then(signedUrl => {
                        if (cancelled) return;
                        if (signedUrl) setSafeSrc(signedUrl, tryFileMetadata);
                        else tryFileMetadata();
                    }).catch((err: unknown) => {
                        if (cancelled) return;
                        const msg = (err as { message?: string } | null)?.message || '';
                        // '(404, cached)' = the negative cache already reported
                        // this key missing once this session — don't re-warn on
                        // every render of every thumbnail of a dead photo.
                        if (!msg.includes('cached)')) console.warn('[usePhotoSrc] S3 URL fetch failed:', msg);
                        // A file_metadata receipt is an independent durable
                        // lookup seam. Use it before session-only URLs so a
                        // stale/missing cached S3 URL cannot blank the photo.
                        tryFileMetadata();
                    });
                }).catch(() => tryFileMetadata());
                return;
            }
            // A cached missing S3 object is terminal for that alias only. The
            // file_metadata receipt remains an independent cloud lookup seam.
            // Priority 5: file_metadata receipt. The cloud persistence
            // contract accepts this as durable even when the project JSON has
            // no S3 key, so the display resolver must do the same.
            tryFileMetadata();
        });

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [expectedProjectId, photo?.data, photo?.dataUrl, photo?.url, photo?.previewUrl, photo?.signedUrl, photo?.thumbnailUrl, photo?.blobId, photo?.s3Key, photo?.fileMetadataId, photo?.unavailableReason, photo?.availability_state, photo?.availability_reason, photo?.availabilityState, photo?.availabilityReason]);

    const resolvedStamp = resolvedStampRef.current;
    return Object.is(resolvedStamp.photo, currentIdentity)
        && resolvedStamp.projectId === expectedProjectId
        ? src
        : null;
}

/**
 * Get a displayable source for a photo synchronously.
 * Falls back gracefully: data URL → null (async paths need the hook).
 * Useful for non-React contexts (e.g., PDF generation, canvas drawing).
 *
 * @param {Object} photo
 * @returns {string|null}
 */
export function getPhotoSrcSync(photo: PhotoLike | null | undefined): string | null {
    if (!photo) return null;
    if (photo.data && typeof photo.data === 'string') return photo.data;
    if (photo.dataUrl && typeof photo.dataUrl === 'string') return photo.dataUrl;
    return null; // Blob/S3 paths require async - use usePhotoSrc hook instead
}

/**
 * DEC-COUNCIL-007 / Candidate C6 Slice 3: detailed variant of usePhotoSrc.
 *
 * Returns `{ src, source, degraded }`:
 *   - src      — string url (same as usePhotoSrc returns)
 *   - source   — one of 'inline' | 'blob' | 's3' | 'thumbnail' | 'session-url' | null
 *                identifies WHICH priority branch produced the src
 *   - degraded — true when the expected source (blob) failed and we fell
 *                back to a later branch (s3 / session-url / nothing)
 *
 * UI can render a small badge / colour when degraded === true so the
 * operator sees that a photo is missing its blob and rendering off a
 * less-canonical source. Today silent degradation is the failure mode
 * Tsunade flagged.
 *
 * @param {Object} photo
 * @param {string|null} expectedProjectId - When set, refuse a blob owned by another project
 * @param {number} attempt - Bump to run the lookup again for the same photo
 * @returns {{src: string|null, source: string|null, degraded: boolean}}
 */
export function usePhotoSrcDetailed(
    photo: PhotoLike | null | undefined,
    expectedProjectId: string | null = null,
    attempt = 0,
): PhotoSrcState {
    const [state, setState] = useState<PhotoSrcState>({ src: null, source: null, degraded: false });
    const currentIdentity = photoIdentity(photo);
    const lastIdentityRef = useRef<{ photo: unknown; projectId: string | null } | undefined>(undefined);
    const resolvedStampRef = useRef<{ photo: unknown; projectId: string | null }>({
        photo: currentIdentity,
        projectId: expectedProjectId,
    });

    useEffect(() => {
        // v5.46.73: clear on photo-identity swap — see usePhotoSrc above.
        const prevIdentity = lastIdentityRef.current;
        const identity = photoIdentity(photo);
        const stamp = { photo: identity, projectId: expectedProjectId };
        const commitState = (next: PhotoSrcState) => {
            resolvedStampRef.current = stamp;
            setState(next);
        };
        lastIdentityRef.current = stamp;
        if (!photo) { commitState({ src: null, source: null, degraded: false }); return; }
        if (!prevIdentity
            || !Object.is(identity, prevIdentity.photo)
            || expectedProjectId !== prevIdentity.projectId) {
            commitState({ src: null, source: null, degraded: false });
        }
        const durableUnavailableReason = candidateFileUnavailableReason(photo);
        // Keep this reason as a degraded-state diagnostic, but do not treat it
        // as a verdict on independent inline/blob/file_metadata sources. A
        // cached S3 miss must never blank a locally recoverable photo.

        let objectUrl: string | null = null;
        let cancelled = false;
        // isUsableDirectUrl from photo-resolution-policy.js (Slice 2 shared).

        // Cross-device guard (2026-06-05): rewrite a stored raw MinIO :9010 URL
        // (unreachable off the host box) to a same-origin proxy URL before
        // display, preserving source/degraded. Non-MinIO URLs set directly.
        const setSafeState = (
            next: PhotoSrcState,
            onUnsafe: () => void = () => commitState({
                src: null,
                source: null,
                degraded: true,
                unavailableReason: next.unavailableReason,
            }),
        ) => {
            if (cancelled) return;
            if (isRawMinioUrl(next.src)) {
                // isRawMinioUrl is false for null, so next.src is a string here.
                import('../services/file-upload-service')
                    .then(({ toSameOriginDisplayUrl }) => toSameOriginDisplayUrl(next.src as string))
                    .then((safe) => {
                        if (cancelled) return;
                        if (safe && !isRawMinioUrl(safe)) commitState({ ...next, src: safe });
                        else onUnsafe();
                    })
                    .catch(() => { if (!cancelled) onUnsafe(); });
            } else {
                commitState(next);
            }
        };

        // (isUsableDirectUrl validates string-ness at runtime; the `as string`
        // casts below are compile-erased and reflect that guarantee.)
        if (isUsableDirectUrl(photo.data) && !isRawMinioUrl(photo.data)) {
            commitState({
                src: photo.data as string,
                source: 'inline',
                degraded: !!durableUnavailableReason,
                unavailableReason: durableUnavailableReason,
            });
            return;
        }
        if (isUsableDirectUrl(photo.dataUrl) && !isRawMinioUrl(photo.dataUrl)) {
            commitState({
                src: photo.dataUrl as string,
                source: 'inline',
                degraded: !!durableUnavailableReason,
                unavailableReason: durableUnavailableReason,
            });
            return;
        }
        // blob is the canonical durable backing source. If it's missing
        // (Dexie wipe, browser-profile change, etc.) and we fall back to
        // s3, mark degraded so the UI can surface it.
        const expectedBlob = !!photo.blobId;
        let blobFound = false;

        const tryBlob: Promise<string | null> = expectedBlob
            ? import('../services/photo-blob-store').then(({ getPhotoBlob }) => {
                if (cancelled) return null;
                return (getPhotoBlob as unknown as GetPhotoBlobFn)(photo.blobId, expectedProjectId).then(blob => {
                    if (cancelled || !blob) return null;
                    objectUrl = URL.createObjectURL(blob);
                    blobFound = true;
                    commitState({
                        src: objectUrl,
                        source: 'blob',
                        degraded: !!durableUnavailableReason,
                        unavailableReason: durableUnavailableReason,
                    });
                    return objectUrl;
                });
            })
            : Promise.resolve(null);

        const setSessionFallback = (
            unavailableReason: string | null = durableUnavailableReason,
            degraded = expectedBlob || !!unavailableReason,
        ) => {
            if (cancelled) return;
            const candidates: Array<{ value: unknown; source: PhotoSrcSource }> = [
                { value: photo.thumbnailUrl, source: 'thumbnail' },
                { value: photo.previewUrl, source: 'session-url' },
                { value: photo.signedUrl, source: 'session-url' },
                { value: photo.url, source: 'session-url' },
                { value: photo.data, source: 'session-url' },
                { value: photo.dataUrl, source: 'session-url' },
            ];
            const tryCandidate = (index: number) => {
                if (cancelled) return;
                const candidate = candidates[index];
                if (candidate && isUsableDirectUrl(candidate.value)) {
                    setSafeState({
                        src: candidate.value as string,
                        source: candidate.source,
                        degraded,
                        unavailableReason,
                    }, () => tryCandidate(index + 1));
                } else if (candidate) {
                    tryCandidate(index + 1);
                } else if (typeof photo.data === 'string' && photo.data.startsWith('blob:')) {
                    commitState({ src: photo.data, source: 'session-url', degraded, unavailableReason });
                } else {
                    commitState({ src: null, source: null, degraded, unavailableReason });
                }
            };
            tryCandidate(0);
        };

        const tryFileMetadata = (
            unavailableReason: string | null = durableUnavailableReason,
            degradedOnFailure = expectedBlob || !!unavailableReason,
        ) => {
            const fileMetadataUnavailableReason = cachedReferenceUnavailableReason(photo.fileMetadataId);
            const effectiveUnavailableReason = unavailableReason || fileMetadataUnavailableReason;
            if (!photo.fileMetadataId || fileMetadataUnavailableReason) {
                setSessionFallback(
                    effectiveUnavailableReason,
                    degradedOnFailure || !!fileMetadataUnavailableReason,
                );
                return;
            }
            getFileMetadataPreviewUrl(photo.fileMetadataId)
                .then((previewUrl) => {
                    if (cancelled) return;
                    if (previewUrl) {
                        setSafeState({
                            src: previewUrl,
                            source: 'file-metadata',
                            degraded: expectedBlob || !!unavailableReason,
                            unavailableReason,
                        }, () => setSessionFallback(effectiveUnavailableReason, degradedOnFailure));
                    } else {
                        setSessionFallback(effectiveUnavailableReason, degradedOnFailure);
                    }
                })
                .catch(() => setSessionFallback(effectiveUnavailableReason, degradedOnFailure));
        };

        tryBlob.finally(() => {
            if (cancelled || blobFound) return;
            // Blob path either wasn't expected or failed → try s3.
            const cachedS3UnavailableReason = cachedReferenceUnavailableReason(photo.s3Key);
            if (photo.s3Key && !cachedS3UnavailableReason) {
                import('../services/file-upload-service').then(({ getFileUrl, getFileReferenceUnavailableReason }) => {
                    if (cancelled) return;
                    (getFileUrl as unknown as GetFileUrlFn)(photo.s3Key).then(signedUrl => {
                        if (cancelled) return;
                        if (signedUrl) setSafeState(
                            { src: signedUrl, source: 's3', degraded: expectedBlob },
                            () => tryFileMetadata(durableUnavailableReason, true),
                        );
                        else tryFileMetadata(durableUnavailableReason, true);
                    }).catch((err: unknown) => {
                        if (cancelled) return;
                        console.warn('[usePhotoSrcDetailed] S3 URL fetch failed:', (err as { message?: string } | null)?.message);
                        const unavailableReason = getFileReferenceUnavailableReason(photo.s3Key)
                            || durableUnavailableReason;
                        tryFileMetadata(unavailableReason, true);
                    });
                }).catch(() => tryFileMetadata(durableUnavailableReason, true));
                return;
            }
            // Do not ask the signer for an alias already proven missing. Keep
            // its reason while trying the independent file_metadata seam.
            tryFileMetadata(cachedS3UnavailableReason || durableUnavailableReason);
        });

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [attempt, expectedProjectId, photo?.data, photo?.dataUrl, photo?.url, photo?.previewUrl, photo?.signedUrl, photo?.thumbnailUrl, photo?.blobId, photo?.s3Key, photo?.fileMetadataId, photo?.unavailableReason, photo?.availability_state, photo?.availability_reason, photo?.availabilityState, photo?.availabilityReason]);

    const resolvedStamp = resolvedStampRef.current;
    return Object.is(resolvedStamp.photo, currentIdentity)
        && resolvedStamp.projectId === expectedProjectId
        ? state
        : { src: null, source: null, degraded: false };
}
